import { Box, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';

import { JOB_TAG_ICON_KEYS, JOB_TAG_ICONS, type JobTagIconKey } from './icons';
import { JobTagIcon } from './job-tag-icon';

interface IconSwatchPickerProps {
  value: string;
  onChange: (icon: JobTagIconKey) => void;
  disabled?: boolean;
  /** Accessible name for the trigger, e.g. `Icon for "Farm Kill"`. */
  label?: string;
  size?: number;
}

/**
 * A square button showing the current icon, opening the curated set as a
 * grid. The sibling of `ColorSwatchPicker` in features/calendar-events — same
 * interaction, icons instead of colours.
 *
 * There is no "custom icon" escape hatch on purpose: the stored value has to
 * be a key this app can render, and the set is kept small enough to stay
 * legible at 16px on a calendar event.
 * @param root0
 * @param root0.value
 * @param root0.onChange
 * @param root0.disabled
 * @param root0.label
 * @param root0.size
 */
export function IconSwatchPicker({
  value,
  onChange,
  disabled = false,
  label = 'Pick an icon',
  size = 32,
}: IconSwatchPickerProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Tooltip title={label}>
        <Box
          component="button"
          type="button"
          aria-label={label}
          disabled={disabled}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            width: size,
            height: size,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            p: 0,
          }}
        >
          <JobTagIcon iconKey={value} fontSize="small" />
        </Box>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack spacing={1} sx={{ p: 1.5, width: 280 }}>
          <Typography variant="caption" color="text.secondary">
            Choose an icon
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.5,
            }}
          >
            {JOB_TAG_ICON_KEYS.map((key) => (
              <Tooltip key={key} title={JOB_TAG_ICONS[key].label}>
                <Box
                  component="button"
                  type="button"
                  aria-label={JOB_TAG_ICONS[key].label}
                  onClick={() => {
                    onChange(key);
                    setAnchor(null);
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    border: key === value ? '2px solid' : '1px solid',
                    borderColor: key === value ? 'primary.main' : 'divider',
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    cursor: 'pointer',
                    p: 0,
                  }}
                >
                  <JobTagIcon iconKey={key} fontSize="small" />
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Stack>
      </Popover>
    </>
  );
}
